import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Loader2, Copy, Check, ShieldAlert, ShieldCheck,
  FileWarning, Network, ListChecks, Target, UserSquare2, ArrowLeftRight,
} from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import type { TransactionRow, ScreenVerdict } from '@/types/walletIntel';
import { VERDICT_COLOR } from '@/types/walletIntel';
import { VerdictBadge } from '@/components/wallet-intel/VerdictBadge';
import { AdmiraltyBadge } from '@/components/wallet-intel/AdmiraltyBadge';
import { EvidenceTrail } from '@/components/wallet-intel/EvidenceTrail';
import { ExposureChain } from '@/components/wallet-intel/ExposureChain';
import { ClusterGraph } from '@/components/wallet-intel/ClusterGraph';
import { IdentityPanel } from '@/components/wallet-intel/IdentityPanel';
import { TransactionsList } from '@/components/wallet-intel/TransactionsList';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';

function detectChain(address: string): string {
  const a = address.trim();
  if (/^0x[a-fA-F0-9]{40}$/.test(a)) return 'ethereum';
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(a)) return 'tron';
  return 'bitcoin';
}

// `needs_review` (a sanctions/exposure check that errored, e.g. Tron's
// current plan-tier gate — see backend's screen_address) used to fall
// through every `=== 'flagged' ? red : green` ternary below and render
// identically to a genuinely clean "clear" result — exactly the
// fail-open-look the backend fix was meant to eliminate. isBad() only
// covers real red-flag verdicts; needs-review gets its own amber styling.
function isBad(verdict: ScreenVerdict): boolean {
  return verdict === 'flagged';
}
function isReview(verdict: ScreenVerdict): boolean {
  return verdict === 'needs_review';
}

const CHAINS = [
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'tron', label: 'Tron' },
];

const TABS = [
  { key: 'overview', label: 'Overview', icon: ListChecks },
  { key: 'identity', label: 'Identity', icon: UserSquare2 },
  { key: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
  { key: 'exposure', label: 'Exposure', icon: Target },
  { key: 'cluster', label: 'Relationships', icon: Network },
  { key: 'evidence', label: 'Evidence', icon: FileWarning },
] as const;
type TabKey = typeof TABS[number]['key'];

function CopyableAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <span style={{ color: '#8D96AC', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{address}</span>
      <button
        onClick={() => { navigator.clipboard.writeText(address); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        style={{ color: copied ? '#00C853' : '#5A7A9A' }}
        className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

export default function WalletProfile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputAddress, setInputAddress] = useState(searchParams.get('address') ?? '');
  const [chain, setChain] = useState(searchParams.get('chain') ?? '');
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [exposureHops, setExposureHops] = useState(1);

  const address = searchParams.get('address') ?? '';
  const activeChain = searchParams.get('chain') || (address ? detectChain(address) : '');

  useEffect(() => {
    if (address) { setInputAddress(address); setChain(activeChain); }
  }, [address, activeChain]);

  // Apply this analyst's saved defaults (Settings page) — only while the page
  // is still blank, so it never overrides an address already in progress.
  const settingsQuery = useQuery({
    queryKey: ['wallet-intel-settings'],
    queryFn: () => walletIntelApi.getSettings(),
  });
  useEffect(() => {
    if (settingsQuery.data && !address && !chain) {
      setChain(settingsQuery.data.default_chain);
      setExposureHops(settingsQuery.data.default_max_hops);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  function runSearch() {
    const addr = inputAddress.trim();
    if (!addr) return;
    const c = chain || detectChain(addr);
    setSearchParams({ address: addr, chain: c });
    setActiveTab('overview');
  }

  const screenQuery = useQuery({
    queryKey: ['wallet-intel-screen', activeChain, address],
    queryFn: () => walletIntelApi.screen({ chain: activeChain, address, include_exposure: true, max_hops: 1 }),
    enabled: !!address,
  });

  const exposureQuery = useQuery({
    queryKey: ['wallet-intel-exposure', activeChain, address, exposureHops],
    queryFn: () => walletIntelApi.exposure({ chain: activeChain, address, max_hops: exposureHops, max_fanout: settingsQuery.data?.default_max_fanout }),
    enabled: !!address && activeTab === 'exposure' && exposureHops !== 1,
  });

  const clusterQuery = useQuery({
    queryKey: ['wallet-intel-cluster', activeChain, address],
    queryFn: () => walletIntelApi.cluster({ chain: activeChain, address, max_hops: 1, max_fanout: settingsQuery.data?.default_max_fanout }),
    enabled: !!address && activeTab === 'cluster',
  });

  const attributionQuery = useQuery({
    queryKey: ['wallet-intel-attribution', activeChain, address],
    queryFn: () => walletIntelApi.attribution(activeChain, address),
    // Eager (not gated by tab) — Overview shows a compact identity summary
    // too, so this needs to be ready by the time someone lands on the page.
    enabled: !!address,
  });

  const transactionsQuery = useQuery({
    queryKey: ['wallet-intel-transactions', activeChain, address],
    queryFn: () => walletIntelApi.transactions({ chain: activeChain, address }),
    enabled: !!address && activeTab === 'transactions',
  });
  const [txRows, setTxRows] = useState<TransactionRow[]>([]);
  const [txCursor, setTxCursor] = useState<string | undefined>(undefined);
  const [txLoadingMore, setTxLoadingMore] = useState(false);

  useEffect(() => {
    if (transactionsQuery.data) {
      setTxRows(transactionsQuery.data.transactions);
      setTxCursor(transactionsQuery.data.next_cursor ?? undefined);
    }
  }, [transactionsQuery.data]);

  async function loadMoreTx() {
    if (!txCursor) return;
    setTxLoadingMore(true);
    try {
      const more = await walletIntelApi.transactions({ chain: activeChain, address, cursor: txCursor });
      setTxRows((prev) => [...prev, ...more.transactions]);
      setTxCursor(more.next_cursor ?? undefined);
    } finally {
      setTxLoadingMore(false);
    }
  }

  const exposureData = exposureHops === 1 ? screenQuery.data?.exposure : exposureQuery.data;
  const screen = screenQuery.data;

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />
      {/* Search bar */}
      <div
        style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }}
        className="flex items-center gap-2 px-4 py-3 mb-6 flex-wrap"
      >
        <Search size={15} style={{ color: '#5A7A9A' }} className="flex-shrink-0" />
        <input
          value={inputAddress}
          onChange={(e) => setInputAddress(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          placeholder="Paste a wallet address…"
          className="flex-1 min-w-[220px] bg-transparent outline-none font-mono text-[13px]"
          style={{ color: '#DCE8F5' }}
        />
        <select
          value={chain || (inputAddress ? detectChain(inputAddress) : 'bitcoin')}
          onChange={(e) => setChain(e.target.value)}
          className="rounded-lg px-2.5 py-1.5 outline-none"
          style={{ background: '#0C1118', border: '1px solid #182030', color: '#8D96AC', fontSize: 12.5, fontWeight: 600 }}
        >
          {CHAINS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <motion.button
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          onClick={runSearch}
          disabled={!inputAddress.trim()}
          className="px-5 py-2 rounded-lg font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontSize: 12.5 }}
        >
          SCREEN
        </motion.button>
      </div>

      {!address && (
        <div style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 16, padding: '64px 32px', textAlign: 'center' }}>
          <Search size={28} style={{ color: '#283A50', margin: '0 auto 14px' }} />
          <p style={{ color: '#8D96AC', fontSize: 13.5 }}>
            Paste an address above to screen it against Sycek's blocklist, live sanctions data, and indirect exposure.
          </p>
        </div>
      )}

      {address && screenQuery.isLoading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin" size={22} style={{ color: '#00C8FF' }} />
        </div>
      )}

      {address && screenQuery.isError && (
        <div style={{ background: '#0C1118', border: '1px solid #FF1F1F30', borderRadius: 12, padding: 24 }}>
          <p style={{ color: '#FF1F1F', fontSize: 13.5 }}>
            {(screenQuery.error as Error)?.message ?? 'Failed to screen this address.'}
          </p>
        </div>
      )}

      {address && screen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          {/* Profile head */}
          <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
            <div className="flex items-center gap-4">
              <div
                style={{
                  width: 56, height: 56, borderRadius: 16, flexShrink: 0,
                  background: `${VERDICT_COLOR[screen.verdict]}18`,
                  border: `1.5px solid ${VERDICT_COLOR[screen.verdict]}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {isBad(screen.verdict) || isReview(screen.verdict)
                  ? <ShieldAlert size={24} style={{ color: VERDICT_COLOR[screen.verdict] }} />
                  : <ShieldCheck size={24} style={{ color: VERDICT_COLOR[screen.verdict] }} />}
              </div>
              <div>
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>
                    Wallet Profile
                  </span>
                  <VerdictBadge verdict={screen.verdict} pulse />
                </div>
                <div className="flex items-center gap-2">
                  <CopyableAddress address={screen.address} />
                  <span style={{ color: '#5A7A9A', fontSize: 12 }}>· {activeChain}</span>
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/app/wallet-intel/reports?address=${encodeURIComponent(address)}&chain=${activeChain}`)}
              className="px-4 py-2.5 rounded-lg font-bold tracking-wide flex items-center gap-2"
              style={{ background: '#FF6B1A18', color: '#FF6B1A', border: '1px solid #FF6B1A44', fontSize: 12.5 }}
            >
              <FileWarning size={14} /> GENERATE SAR/STR
            </motion.button>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'DIRECT CITATIONS', value: screen.direct_match.length },
              { label: 'SANCTIONED', value: screen.sanctioned?.isSanctioned ? 'YES' : 'NO', color: screen.sanctioned?.isSanctioned ? '#FF1F1F' : '#00C853' },
              { label: 'ADMIRALTY GRADE', value: screen.direct_match.length ? `${screen.admiralty_grade.source_reliability}${screen.admiralty_grade.information_credibility}` : '—' },
              { label: 'REASONS', value: screen.reasons.length },
            ].map((k) => (
              <motion.div key={k.label} whileHover={{ y: -2 }} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ color: k.color ?? '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 22 }}>{k.value}</div>
                <div style={{ color: '#7C8CA6', fontSize: 11, marginTop: 4, letterSpacing: '0.04em', fontWeight: 600 }}>{k.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5 overflow-x-auto" style={{ borderBottom: '1px solid #182030' }}>
            {TABS.map((t) => {
              const active = activeTab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className="flex items-center gap-2 px-3.5 py-3 transition-colors flex-shrink-0"
                  style={{
                    color: active ? '#00C8FF' : '#8D96AC',
                    borderBottom: `2px solid ${active ? '#00C8FF' : 'transparent'}`,
                    fontWeight: active ? 700 : 500,
                    fontSize: 13.5,
                  }}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {activeTab === 'overview' && (
                <div className="flex flex-col gap-5">
                  {/* One-line plain-language summary */}
                  <div
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
                    style={{
                      background: isBad(screen.verdict) ? '#1A0000' : isReview(screen.verdict) ? '#1F1600' : '#001F0F',
                      border: `1px solid ${VERDICT_COLOR[screen.verdict]}40`,
                    }}
                  >
                    {isBad(screen.verdict) || isReview(screen.verdict)
                      ? <ShieldAlert size={20} style={{ color: VERDICT_COLOR[screen.verdict], flexShrink: 0 }} />
                      : <ShieldCheck size={20} style={{ color: VERDICT_COLOR[screen.verdict], flexShrink: 0 }} />}
                    <div>
                      <p style={{ color: VERDICT_COLOR[screen.verdict], fontWeight: 700, fontSize: 14.5 }}>
                        {isBad(screen.verdict)
                          ? `Flagged: ${screen.reasons.map((r) => r.replace(/_/g, ' ')).join(', ')}`
                          : isReview(screen.verdict)
                          ? `Needs review: ${screen.reasons.map((r) => r.replace(/_/g, ' ')).join(', ') || 'a live check could not complete'}`
                          : 'No red flags — no blocklist match, no sanctions hit, no known-bad address nearby (1 hop)'}
                      </p>
                      <p style={{ color: '#8D96AC', fontSize: 12, marginTop: 4 }}>
                        {isBad(screen.verdict)
                          ? 'See the Evidence and Exposure tabs for exactly what was found and why.'
                          : isReview(screen.verdict)
                          ? 'A sanctions or exposure check errored or is unavailable on the current plan — this is NOT a clean result. Retry later or verify manually before relying on it.'
                          : '"Clear" means nothing in our data or a live sanctions check raised a flag — not an independent guarantee of legitimacy.'}
                      </p>
                    </div>
                  </div>

                  {/* Compact identity summary, if available */}
                  {attributionQuery.data && (
                    <motion.button
                      whileHover={{ scale: 1.005, borderColor: '#00C8FF44' }}
                      onClick={() => setActiveTab('identity')}
                      className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-left w-full"
                      style={{ background: '#0C1118', border: '1px solid #182030' }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#00C8FF18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <UserSquare2 size={17} style={{ color: '#00C8FF' }} />
                        </div>
                        <span style={{ color: '#F2F6FC', fontSize: 13 }}>
                          {(() => {
                            const labels = [...new Set(attributionQuery.data.breadcrumbs.map((b) => b.entity_label).filter(Boolean))];
                            return labels.length > 0
                              ? <>Identified as <strong>{labels[0]}</strong> · {attributionQuery.data.info.total_tx_count.toLocaleString()} total transactions</>
                              : <>No known entity label · {attributionQuery.data.info.total_tx_count.toLocaleString()} total transactions</>;
                          })()}
                        </span>
                      </div>
                      <span style={{ color: '#00C8FF', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>View Identity →</span>
                    </motion.button>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '20px 22px' }}>
                      <p className="tracking-[0.12em] mb-4" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>SCREENING REASONS</p>
                      {screen.reasons.length === 0 ? (
                        <p style={{ color: '#5A7A9A', fontSize: 13 }}>No flags raised for this address.</p>
                      ) : (
                        <div className="flex flex-col gap-2.5">
                          {screen.reasons.map((r) => (
                            <div key={r} className="flex items-center gap-2.5">
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#FF1F1F', flexShrink: 0 }} />
                              <span style={{ color: '#DCE8F5', fontSize: 13 }}>{r.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '20px 22px' }}>
                      <p className="tracking-[0.12em] mb-4" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>SANCTIONS CHECK</p>
                      {screen.sanctioned ? (
                        <div className="flex items-center gap-2.5">
                          {screen.sanctioned.isSanctioned
                            ? <ShieldAlert size={18} style={{ color: '#FF1F1F' }} />
                            : <ShieldCheck size={18} style={{ color: '#00C853' }} />}
                          <span style={{ color: '#DCE8F5', fontSize: 14 }}>
                            {screen.sanctioned.isSanctioned ? 'Sanctions match found' : 'No sanctions match'}
                          </span>
                        </div>
                      ) : (
                        <p style={{ color: '#5A7A9A', fontSize: 13 }}>Live sanctions check unavailable.</p>
                      )}
                      <p className="mt-3" style={{ color: '#3A5A7A', fontSize: 11 }}>
                        Live watchlist check (OFAC/SDN and related lists) · {screen.breadcrumbs_calls_used} check(s) run
                      </p>
                      {screen.direct_match.length > 0 && (
                        <div className="mt-4 flex items-center gap-2">
                          <span style={{ color: '#7C8CA6', fontSize: 11, fontWeight: 600 }}>OVERALL GRADE</span>
                          <AdmiraltyBadge grade={screen.admiralty_grade} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'identity' && (
                attributionQuery.isLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: '#00C8FF' }} /></div>
                ) : attributionQuery.isError ? (
                  <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 12 }}>
                    {(attributionQuery.error as Error)?.message ?? 'Failed to load attribution data.'}
                  </p>
                ) : attributionQuery.data ? (
                  <IdentityPanel attribution={attributionQuery.data} />
                ) : null
              )}

              {activeTab === 'transactions' && (
                transactionsQuery.isLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: '#00C8FF' }} /></div>
                ) : transactionsQuery.isError ? (
                  <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 12 }}>
                    {(transactionsQuery.error as Error)?.message ?? 'Failed to load transactions.'}
                  </p>
                ) : (
                  <TransactionsList
                    address={address}
                    chain={activeChain}
                    transactions={txRows}
                    hasMore={!!txCursor}
                    onLoadMore={loadMoreTx}
                    isLoadingMore={txLoadingMore}
                  />
                )
              )}

              {activeTab === 'exposure' && (
                exposureQuery.isError ? (
                  <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 12 }}>Failed to run exposure check.</p>
                ) : exposureData ? (
                  <ExposureChain
                    exposure={exposureData}
                    seedAddress={address}
                    maxHops={exposureHops}
                    onMaxHopsChange={setExposureHops}
                    isFetching={exposureQuery.isFetching}
                  />
                ) : (
                  <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: '#00C8FF' }} /></div>
                )
              )}

              {activeTab === 'cluster' && (
                clusterQuery.isLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: '#00C8FF' }} /></div>
                ) : clusterQuery.isError ? (
                  <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 12 }}>Failed to load relationship graph.</p>
                ) : clusterQuery.data ? (
                  <ClusterGraph cluster={clusterQuery.data} chain={activeChain} />
                ) : null
              )}

              {activeTab === 'evidence' && (
                <div className="flex flex-col gap-4">
                  {screen.direct_match.length === 0 && (
                    <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '14px 18px' }}>
                      <p style={{ color: '#8D96AC', fontSize: 12.5, lineHeight: 1.6 }}>
                        No direct citations means no source across our 37.5M+ aggregated wallet-tag dataset has flagged this exact address
                        — it does <strong style={{ color: '#DCE8F5' }}>not</strong> mean the address has been independently verified as safe.
                        Check the Exposure and Identity tabs for what else is known.
                      </p>
                    </div>
                  )}
                  <EvidenceTrail matches={screen.direct_match} />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
